import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { BaseGameProps, GameStatus } from "../common/types";

const WINNING_COMBINATIONS = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8], // Yatay
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8], // Dikey
  [0, 4, 8],
  [2, 4, 6], // Çapraz
];

function getEmptyIndices(board: Array<string>): number[] {
  return board
    .map((cell, index) => (cell === "" ? index : -1))
    .filter((index) => index !== -1);
}

function pickRandom(indices: number[]): number {
  if (indices.length === 0) return -1;
  return indices[Math.floor(Math.random() * indices.length)] ?? -1;
}

/** `mark` için bir hamlede kazandıran hücreyi döndürür (yoksa -1). */
function findWinningMove(board: Array<string>, mark: string): number {
  for (const index of getEmptyIndices(board)) {
    const tempBoard = [...board];
    tempBoard[index] = mark;
    for (const combo of WINNING_COMBINATIONS) {
      if (
        tempBoard[combo[0]] === mark &&
        tempBoard[combo[1]] === mark &&
        tempBoard[combo[2]] === mark
      ) {
        return index;
      }
    }
  }
  return -1;
}

/**
 * Kolay AI — gerçekten yenilebilir.
 * Kazanma fırsatını çoğu zaman kaçırır, tehditleri çoğunlukla görmez,
 * merkez/köşe tercihi yoktur. Amaç oyuncunun kazanabilmesi.
 */
function easyAI(board: Array<string>): number {
  const empty = getEmptyIndices(board);
  if (empty.length === 0) return -1;

  const winMove = findWinningMove(board, "O");
  if (winMove !== -1 && Math.random() < 0.35) return winMove;

  const blockMove = findWinningMove(board, "X");
  if (blockMove !== -1 && Math.random() < 0.3) return blockMove;

  return pickRandom(empty);
}

/**
 * Orta AI — kazanır ve bloke eder ama ileriyi görmez.
 * Çatal (fork) kurulumlarına karşı savunmasız; dikkatli oyuncu kazanabilir.
 */
function mediumAI(board: Array<string>): number {
  const empty = getEmptyIndices(board);
  if (empty.length === 0) return -1;

  const winMove = findWinningMove(board, "O");
  if (winMove !== -1) return winMove;

  const blockMove = findWinningMove(board, "X");
  if (blockMove !== -1 && Math.random() < 0.85) return blockMove;

  // Merkez ve köşeleri hafif tercih et, ama rastgelelik bırak
  if (board[4] === "" && Math.random() < 0.5) return 4;
  const corners = [0, 2, 6, 8].filter((i) => board[i] === "");
  if (corners.length > 0 && Math.random() < 0.5) return pickRandom(corners);

  return pickRandom(empty);
}

/**
 * Minimax algoritması kullanarak TicTacToe AI
 * @param board - 3x3 oyun tahtasını temsil eden dizi
 * @returns Yapay zekanın (O) oynaması gereken hücrenin indeksini döndürür
 */
function minimaxAI(board: Array<string>, maxDepth = 12): number {
  // Boş hücreleri bul
  const emptyIndices = board
    .map((cell, index) => (cell === "" ? index : -1))
    .filter((index) => index !== -1);

  if (emptyIndices.length === 0) return -1; // Tahta doluysa -1 döndür

  // İlk hamle için optimizasyon: eğer tahta boşsa, köşeyi veya ortayı seç
  if (emptyIndices.length === 9) {
    const firstMoves = [0, 2, 4, 6, 8]; // Köşeler veya orta
    return firstMoves[Math.floor(Math.random() * firstMoves.length)];
  }

  // Kazanan kontrolü
  function checkWinner(board: Array<string>): string | null {
    const winningCombinations = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8], // Yatay
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8], // Dikey
      [0, 4, 8],
      [2, 4, 6], // Çapraz
    ];

    for (const combo of winningCombinations) {
      if (
        board[combo[0]] !== "" &&
        board[combo[0]] === board[combo[1]] &&
        board[combo[1]] === board[combo[2]]
      ) {
        return board[combo[0]];
      }
    }

    return board.includes("") ? null : "tie";
  }

  // Minimax algoritması
  function minimax(
    board: Array<string>,
    depth: number,
    isMaximizing: boolean
  ): number {
    const winner = checkWinner(board);

    // Terminal durumlar
    if (winner === "O") return 10 - depth; // AI kazandı
    if (winner === "X") return depth - 10; // İnsan kazandı
    if (winner === "tie") return 0; // Beraberlik
    if (depth >= maxDepth) return 0;

    const empty = board
      .map((cell, index) => (cell === "" ? index : -1))
      .filter((index) => index !== -1);

    if (isMaximizing) {
      let bestScore = -Infinity;
      for (const index of empty) {
        board[index] = "O";
        const score = minimax(board, depth + 1, false);
        board[index] = "";
        bestScore = Math.max(score, bestScore);
      }
      return bestScore;
    } else {
      let bestScore = Infinity;
      for (const index of empty) {
        board[index] = "X";
        const score = minimax(board, depth + 1, true);
        board[index] = "";
        bestScore = Math.min(score, bestScore);
      }
      return bestScore;
    }
  }

  // En iyi hamleyi bul
  let bestScore = -Infinity;
  let bestMove = -1;

  for (const index of emptyIndices) {
    board[index] = "O";
    const score = minimax([...board], 0, false);
    board[index] = "";

    if (score > bestScore) {
      bestScore = score;
      bestMove = index;
    }
  }

  return bestMove;
}

export function TicTacToe({
  className = "",
  difficulty = "medium",
  onGameEnd,
}: BaseGameProps) {
  const { t } = useTranslation();
  const [board, setBoard] = useState<string[]>(Array(9).fill(""));
  const [isPlayerTurn, setIsPlayerTurn] = useState<boolean>(true);
  const [gameStatus, setGameStatus] = useState<GameStatus>("playing");
  const [winningCells, setWinningCells] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Kazanan kontrolü
  const checkWinner = (
    board: string[]
  ): { winner: string | null; winningCombination: number[] | null } => {
    const winningCombinations = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8], // Yatay
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8], // Dikey
      [0, 4, 8],
      [2, 4, 6], // Çapraz
    ];

    for (const combo of winningCombinations) {
      if (
        board[combo[0]] !== "" &&
        board[combo[0]] === board[combo[1]] &&
        board[combo[1]] === board[combo[2]]
      ) {
        return { winner: board[combo[0]], winningCombination: combo };
      }
    }

    // Beraberlik kontrolü
    if (!board.includes("")) {
      return { winner: "tie", winningCombination: null };
    }

    return { winner: null, winningCombination: null };
  };

  // Oyunu sıfırla
  const resetGame = () => {
    setBoard(Array(9).fill(""));
    setIsPlayerTurn(true);
    setGameStatus("playing");
    setWinningCells([]);
    setLoading(false);
  };

  // Kazanan durumunu işle
  const handleGameEnd = (status: GameStatus) => {
    setGameStatus(status);
    if (onGameEnd) {
      onGameEnd(status);
    }
  };

  // AI hamlesi yap
  const makeAIMove = () => {
    if (gameStatus !== "playing" || isPlayerTurn) return;

    setLoading(true);

    // Küçük bir gecikme ekleyerek AI düşünüyormuş gibi görünmesini sağlayalım
    setTimeout(() => {
      const aiMove =
        difficulty === "easy"
          ? easyAI([...board])
          : difficulty === "medium"
            ? mediumAI([...board])
            : minimaxAI([...board]);

      if (aiMove !== -1) {
        const newBoard = [...board];
        newBoard[aiMove] = "O";
        setBoard(newBoard);

        // Kazanan kontrolü
        const { winner, winningCombination } = checkWinner(newBoard);
        if (winner) {
          if (winner === "O") {
            handleGameEnd("lose");
            if (winningCombination) setWinningCells(winningCombination);
          } else if (winner === "tie") {
            handleGameEnd("draw");
          }
        } else {
          setIsPlayerTurn(true);
        }
      }

      setLoading(false);
    }, 500);
  };

  // Oyuncunun hamlesi
  const handleCellClick = (index: number) => {
    if (
      board[index] !== "" ||
      gameStatus !== "playing" ||
      !isPlayerTurn ||
      loading
    )
      return;

    const newBoard = [...board];
    newBoard[index] = "X";
    setBoard(newBoard);

    // Kazanan kontrolü
    const { winner, winningCombination } = checkWinner(newBoard);
    if (winner) {
      if (winner === "X") {
        handleGameEnd("win");
        if (winningCombination) setWinningCells(winningCombination);
      } else if (winner === "tie") {
        handleGameEnd("draw");
      }
    } else {
      setIsPlayerTurn(false);
    }
  };

  // AI hamlesi için useEffect
  useEffect(() => {
    if (!isPlayerTurn && gameStatus === "playing") {
      makeAIMove();
    }
  }, [isPlayerTurn, gameStatus]);

  // İlk oyun başlangıcı için
  useEffect(() => {
    resetGame();
  }, [difficulty]);

  // Oyun durumu mesajı
  const getStatusMessage = () => {
    switch (gameStatus) {
      case "win":
        return t("games.ticTacToe.status.win");
      case "lose":
        return t("games.ticTacToe.status.lose");
      case "draw":
        return t("games.ticTacToe.status.draw");
      default:
        return isPlayerTurn
          ? t("games.ticTacToe.status.yourTurn")
          : t("games.ticTacToe.status.aiThinking");
    }
  };

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col items-center justify-center px-4 py-3",
        className
      )}
    >
      <div className="mb-3 h-6 text-center">
        <p
          className={cn(
            "font-medium",
            gameStatus === "win"
              ? "text-green-500"
              : gameStatus === "lose"
              ? "text-red-500"
              : ""
          )}
        >
          {getStatusMessage()}
        </p>
      </div>

      <div className="aspect-square w-full max-w-[min(22rem,100%)] overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="w-full h-full grid grid-cols-3 grid-rows-3">
          {board.map((cell, index) => {
            // Hücre pozisyonunu belirle (satır, sütun)
            const col = index % 3;
            const row = Math.floor(index / 3);

            return (
              <button
                key={index}
                className={cn(
                  "relative flex items-center justify-center transition-all",
                  // Alt kenar - en alt satır hariç
                  row < 2
                    ? "border-b border-gray-300 dark:border-gray-600"
                    : "",
                  // Sağ kenar - en sağ sütun hariç
                  col < 2
                    ? "border-r border-gray-300 dark:border-gray-600"
                    : "",
                  winningCells.includes(index)
                    ? "bg-green-100/30 dark:bg-green-900/30"
                    : "",
                  gameStatus === "playing" && cell === "" && isPlayerTurn
                    ? "hover:bg-gray-100 dark:hover:bg-gray-700"
                    : ""
                )}
                onClick={() => handleCellClick(index)}
                disabled={gameStatus !== "playing" || !isPlayerTurn || loading}
              >
                {cell === "X" && (
                  <div className="flex h-full w-full items-center justify-center text-blue-500">
                    <svg
                      viewBox="0 0 60 60"
                      className="h-[55%] w-[55%] max-h-[72px] max-w-[72px]"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 15L45 45M15 45L45 15"
                        stroke="currentColor"
                        strokeWidth="8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                )}

                {cell === "O" && (
                  <div className="flex h-full w-full items-center justify-center text-orange-400">
                    <svg
                      viewBox="0 0 60 60"
                      className="h-[55%] w-[55%] max-h-[72px] max-w-[72px]"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="30"
                        cy="30"
                        r="18"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                      />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {gameStatus !== "playing" && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="mt-4 h-9 px-4 rounded-lg sm:mt-6"
          onClick={resetGame}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          {t("games.ticTacToe.restart")}
        </Button>
      )}

      <div className="mt-3 hidden text-xs text-muted-foreground sm:block">
        <p>
          {t("games.ticTacToe.difficultyLabel")}:{" "}
          {difficulty === "easy"
            ? t("games.difficulty.easy")
            : difficulty === "hard"
              ? t("games.difficulty.hard")
              : t("games.difficulty.medium")}
        </p>
        <p>
          {t("games.ticTacToe.playerLabel")}: X |{" "}
          {t("games.ticTacToe.aiLabel")}: O
        </p>
      </div>
    </div>
  );
}
